/**
 * Central query key factories — stable shapes help cache invalidation and match Orval-style codegen later.
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */

/** Company plans via `/api/File/GetAllFilesForCompany/...`. */
export const companyFilesQueryKeys = {
    all: () => ['companyFiles'],
    list: (companyId, customerId, page, pageSize) => [
        ...companyFilesQueryKeys.all(),
        'list',
        { companyId, customerId, page, pageSize },
    ],
};

export const queryKeys = {
    companyFiles: companyFilesQueryKeys,
    invoices: {
        all: () => ['invoices'],
        company: (companyId, customerId, startDate, endDate, page, pageSize) => [
            ...queryKeys.invoices.all(),
            'company',
            { companyId, customerId, startDate, endDate, page, pageSize },
        ],
        customer: (companyId, customerId, startDate, endDate, page, pageSize) => [
            ...queryKeys.invoices.all(),
            'customer',
            { companyId, customerId, startDate, endDate, page, pageSize },
        ],
        botmark: (companyId, customerId, startDate, endDate, page, pageSize) => [
            ...queryKeys.invoices.all(),
            'botmark',
            { companyId, customerId, startDate, endDate, page, pageSize },
        ],
        reference: (companyId, customerId) => [
            ...queryKeys.invoices.all(),
            'reference',
            { companyId, customerId },
        ],
    },
    files: {
        all: () => ['files'],
        customerList: (companyId, customerId, page, pageSize) => [
            ...queryKeys.files.all(),
            'customer',
            { companyId, customerId, page, pageSize },
        ],
        count: (companyId) => [...queryKeys.files.all(), 'count', companyId],
    },
    messages: {
        all: () => ['messages'],
        company: (companyId) => [...queryKeys.messages.all(), 'company', companyId],
        user: (id) => [...queryKeys.messages.all(), 'user', id],
    },
    licenses: {
        all: () => ['licenses'],
        list: (companyId, type) => [...queryKeys.licenses.all(), { companyId, type }],
    },
    companyUsers: {
        all: () => ['companyUsers'],
        list: (companyId, menuType) => [...queryKeys.companyUsers.all(), { companyId, menuType }],
        selected: (companyId) => [...queryKeys.companyUsers.all(), 'selected', companyId],
    },
    rulers: {
        all: () => ['rulers'],
        list: () => [...queryKeys.rulers.all(), 'all'],
        forCompany: (companyId) => [...queryKeys.rulers.all(), 'company', companyId],
    },
    surveys: {
        all: () => ['surveys'],
        customer: (companyId) => [...queryKeys.surveys.all(), 'customer', companyId],
        processor: (companyId) => [...queryKeys.surveys.all(), 'processor', companyId],
    },
    companies: {
        all: () => ['companies'],
        list: () => [...queryKeys.companies.all(), 'list'],
        filtered: (startDate, endDate) => [
            ...queryKeys.companies.all(),
            'filtered',
            { startDate, endDate },
        ],
        detail: (companyId) => [...queryKeys.companies.all(), 'detail', companyId],
        processorList: () => [...queryKeys.companies.all(), 'processors'],
        botmarkInfo: () => [...queryKeys.companies.all(), 'botmarkInfo'],
    },
    prices: {
        all: () => ['prices'],
        company: (companyId) => [...queryKeys.prices.all(), 'company', companyId],
        botmark: () => [...queryKeys.prices.all(), 'botmark'],
    },
    roles: {
        all: () => ['roles'],
        list: () => [...queryKeys.roles.all(), 'list'],
    },
    permissions: {
        all: () => ['permissions'],
        matrix: () => [...queryKeys.permissions.all(), 'matrix'],
        me: () => [...queryKeys.permissions.all(), 'me'],
        roleUserCounts: () => [...queryKeys.permissions.all(), 'roleUserCounts'],
        roleUsers: (roleId) => [...queryKeys.permissions.all(), 'roleUsers', roleId],
    },
    saConfig: {
        all: () => ['saConfig'],
        admin: () => [...queryKeys.saConfig.all(), 'admin'],
        public: () => [...queryKeys.saConfig.all(), 'public'],
    },
    account: {
        all: () => ['account'],
        processorVersion: () => [...queryKeys.account.all(), 'processorVersion'],
    },
    apps: {
        all: () => ['apps'],
        urls: () => [...queryKeys.apps.all(), 'urls'],
    },
    tutorials: {
        all: () => ['tutorials'],
        videos: () => [...queryKeys.tutorials.all(), 'videos'],
        categories: () => [...queryKeys.tutorials.all(), 'categories'],
    },
};
